import alt from 'components/Dispatcher';
import {Store, StoreMethods} from '@store/@types/alt';
import KpiGoalActions, {KpiTargetActionsInterface} from './KpiTargetActions';
import {
  KpiAssignment,
  KpiAssignmentId,
  KpiOrderBy,
  KpiTarget,
  KpiTargetId,
  KpiTargetTree,
} from '../@types/kpi';
import {List, Map, Set} from 'immutable';
import {associateBy} from 'utils/associateBy';
import {UserId} from 'records/User';

export interface KpiTargetStoreState {
  kpiTargetTree: List<KpiTargetTree>;
  kpisById: Map<KpiTargetId, KpiTarget>;
  selectedKpis: Set<KpiTargetId>;
  selectedGoal?: KpiTargetId;
  assignments: List<KpiAssignment>;
  assignmentsByKpiId: Map<KpiTargetId, List<KpiAssignment>>;
  order: 'asc' | 'desc';
  orderBy: KpiOrderBy;
  assignedKpisByUser: Map<UserId, List<KpiTarget>>;
  completedAssignments: Map<KpiAssignmentId, boolean>;
}

/** @link KpiTargetSource
 *  @link KpiTargetActions */
class KpiTargetStore
  extends Store<KpiTargetStoreState>
  implements StoreMethods<KpiTargetActionsInterface>
{
  constructor() {
    super();
    this.state = {
      kpiTargetTree: List(),
      kpisById: Map(),
      selectedKpis: Set(),
      selectedGoal: undefined,
      assignments: List(),
      assignmentsByKpiId: Map(),
      // TODO This should be component state
      order: 'asc',
      // TODO This should be component state
      orderBy: 'name',
      assignedKpisByUser: Map(),
      completedAssignments: Map(),
    };
    this.bindActions(KpiGoalActions);
  }

  setOrder(property: KpiOrderBy) {
    const prevOrderBy = this.state.orderBy;
    const prevOrder = this.state.order;
    if (prevOrderBy === property) {
      this.setState({order: prevOrder === 'asc' ? 'desc' : 'asc'});
    } else {
      this.setState({
        order: 'asc',
        orderBy: property,
      });
    }
  }

  unsetKpiSelections() {
    this.setState({
      selectedGoal: undefined,
      selectedKpis: Set(),
    });
  }

  toggleKpi(id: KpiTargetId) {
    const {selectedGoal, selectedKpis, kpiTargetTree} = this.state;
    const kpi = kpiTargetTree.find(t => t.kpiTarget.id === id);

    // Note: if this kpi is a child of a goal, it won't show up in this find.
    // That's fine though, because a child kpi cannot be a goal.
    // Do NOT change this to kpi?.kpiChildren. It matters if this isn't defined.
    if (kpi && !kpi.kpiChildren.isEmpty()) {
      // If this kpi has children, then it's a goal. You can only have
      // one goal selected at a time. Overwrite or toggle the existing
      // goal selection if any exists.
      return this.setState({
        selectedGoal: selectedGoal === id ? undefined : id,
      });
    }

    // otherwise add it to the set
    const kpis = selectedKpis.has(id) ? selectedKpis.remove(id) : selectedKpis.add(id);
    this.setState({selectedKpis: kpis});
  }

  fetchGoalKpiTree(): void {}
  fetchGoalKpiTreeError(error: Error): void {}
  fetchGoalKpiTreeSuccess(kpiTargetTree: List<KpiTargetTree>): void {
    const kpis = kpiTargetTree.flatMap(t => t.kpiChildren.push(t.kpiTarget));
    this.setState({kpiTargetTree, kpisById: associateBy(kpis, k => k.id)});
  }

  fetchUsedByDateAndTeams(): void {}
  fetchUsedByDateAndTeamsError(error: Error): void {}
  fetchUsedByDateAndTeamsSuccess(kpis: KpiTarget[]): void {
    this.setState({kpisById: associateBy(kpis, k => k.id)});
  }

  create(): void {}
  createError(error: Error): void {}
  createSuccess(kpi: KpiTarget): void {}

  delete(): void {}
  deleteError(error: Error): void {}
  deleteSuccess(): void {}

  update(): void {}
  updateError(error: Error): void {}
  updateSuccess(kpi: KpiTarget): void {}

  allAssignments(): void {}
  allAssignmentsError(error: Error): void {
    console.log(error);
  }
  allAssignmentsSuccess(assignments: List<KpiAssignment>): void {
    this.setState({
      assignments: assignments,
      assignmentsByKpiId: assignments.groupBy(a => a.kpiId),
    });
  }

  createAssignment(): void {}
  createAssignmentError(error: Error): void {}
  createAssignmentSuccess(assignment: KpiAssignment): void {}

  deleteAssignment(): void {}
  deleteAssignmentError(error: Error): void {}
  deleteAssignmentSuccess(): void {}

  updateAssignment(): void {}
  updateAssignmentError(error: Error): void {}
  updateAssignmentSuccess(assignment: KpiAssignment): void {}

  forceCompleteAssignment(): void {}
  forceCompleteAssignmentError(error: Error): void {}
  forceCompleteAssignmentSuccess(args: any): void {}

  completedAssignments(): void {}
  completedAssignmentsError(error: Error): void {}
  completedAssignmentsSuccess(completions: Map<KpiAssignmentId, boolean>): void {
    this.setState({
      completedAssignments: completions,
    });
  }
}

export default alt.createStore(KpiTargetStore);
